import React, { useEffect, useState } from 'react';
import {
    convertV2HandsLog,
    getV2PoseLogDimensions,
    MediaPipeHandAccessor,
    UnifiedHandKeypoints,
    ExecutableAssessment,
    HandsLogV2Schema,
} from '@exerai/vision-engine-web';
import { ArtifactType } from '@/common/const';
import { useHttpContext } from '@/common/hooks/HttpContext';
import { Nullish } from '@/common/types';
import { LoadingSpinner } from '@/components/common/LoadingSpinner/LoadingSpinner';
import { useAssessmentsContext } from '@/components/context/hooks/AssessmentsContext';
import { resultIsError, Result } from '@/services/HttpService';
import { HandsSessionReplay } from './HandsSessionReplay';

interface HandsSessionReplayProps {
    sessionUUID: string;
    assessmentId: string;
}

export const HandsSessionReplayContainer = (props: HandsSessionReplayProps) => {
    const { sessionUUID, assessmentId } = props;
    const { httpService } = useHttpContext();
    const { assessmentsDict } = useAssessmentsContext();
    const [poseDimensions, setPoseDimensions] = useState<{ width: number; height: number }>({
        width: 900,
        height: 640,
    });
    const [handsLog, setHandsLog] = useState<Nullish<UnifiedHandKeypoints[][]>>();
    const [assessmentDefinition, setAssessmentDefinition] = useState<ExecutableAssessment>();
    const [errorString, setErrorString] = useState<string>();

    useEffect(() => {
        (async () => {
            const res = await httpService.adminGetScanSessionArtifactSignedUrl(sessionUUID, ArtifactType.HandsLog);
            if (resultIsError(res)) {
                setErrorString(res.message);
                return;
            }
            const handsRes: Result<HandsLogV2Schema> = await httpService.getExternalUrl(res);
            if (resultIsError(handsRes)) {
                setErrorString(handsRes.message);
                return;
            }

            const handAccessor = MediaPipeHandAccessor;

            const dimensions = getV2PoseLogDimensions(handsRes);
            if (dimensions) {
                setPoseDimensions(dimensions);
            }
            const convertedHandsLog = convertV2HandsLog(handsRes, handAccessor);
            if (handsRes.meta.scanVersion) {
                setHandsLog(convertedHandsLog);
            } else {
                setErrorString('Cannot replay for sessions < 2.18.0');
            }

            assessmentsDict &&
                assessmentsDict[assessmentId].directives &&
                setAssessmentDefinition(assessmentsDict[assessmentId] as ExecutableAssessment);
        })();
    }, [assessmentId, assessmentsDict, httpService, sessionUUID]);

    return handsLog && assessmentDefinition ? (
        <>
            <HandsSessionReplay
                poseDimensions={poseDimensions}
                handsLog={handsLog}
                assessmentDefinition={assessmentDefinition}
            />
        </>
    ) : errorString ? (
        <div>{errorString}</div>
    ) : (
        <LoadingSpinner />
    );
};
